/**
 * Every link this ring hands out, in one place.
 *
 * A link is a key, and there is one for each thing an owner might want to give
 * away: the ring itself, read-only; one circle of it; a subscribe address for
 * a calendar app; a collect link that lets people add themselves; and the
 * owner link, which is the key to all of it. They used to live in three
 * different places. The front page presents them as one idea, and so does
 * this dialog — the Keys panel keeps the account and the claim, and shows the
 * owner link too, through the same controls (ui/owner-link.ts).
 */
import type { Calendar, IcsAlarm, Source, Tag, Visibility } from '@circle/shared';
import { api } from '../app/api';
import { button, copyRow, el, setChildren, type Child } from './dom';
import { confirmDialog, openDialog } from './dialog';
import { toast, toastError } from './toast';
import { circleShareRow } from './circle-share';
import { buildCircleTree, flattenTree } from './circles';
import { icsUrls, shareUrl } from './share-links';
import { ownerLinkActions, ownerLinkRow } from './owner-link';
import { collectLinkControls } from './sources-panel';

export { icsUrls, shareUrl };

export interface ShareDialogOptions {
  calendar: Calendar;
  onUpdated: (calendar: Calendar) => void;
  /** Read live, so a circle shared from here shows its link once the bundle is back. */
  tags?: () => Tag[];
  /** Refetch the bundle after a circle's link changed. */
  onTagsChanged?: () => Promise<void>;
  /** Read live: the collect links are one per collect source. */
  sources?: () => Source[];
  /** A collect link was replaced; the page keeps the returned source. */
  onSourceUpdated?: (source: Source) => void;
}

/**
 * Private, or open by link. The contract also knows `public` — "may be
 * listed" — but nothing lists anything, so it is not offered; a calendar that
 * already carries it is shown, and behaves, as open by link.
 */
const VISIBILITY: { value: Visibility; label: string; hint: string }[] = [
  { value: 'private', label: 'Only me', hint: 'Nobody else can open the ring. Any link you shared stops working.' },
  { value: 'link', label: 'Anyone with the link', hint: 'The ring opens read-only for whoever has the link. It is not listed anywhere.' },
];

const ALARMS: { value: IcsAlarm; label: string }[] = [
  { value: 'none', label: 'No reminder' },
  { value: 'same-day-0900', label: 'Remind me at 09:00 on the day' },
  { value: 'day-before-0900', label: 'Remind me at 09:00 the day before' },
];

export function openShareDialog(options: ShareDialogOptions): void {
  let calendar = options.calendar;

  const handle = openDialog({ title: 'Share this ring', wide: true, body: [] });
  const render = (): void => {
    setChildren(handle.body, content());
  };
  const updated = (next: Calendar): void => {
    calendar = next;
    options.onUpdated(calendar);
    render();
  };

  const setVisibility = async (visibility: Visibility): Promise<void> => {
    try {
      updated(await api.share(calendar.id, visibility));
    } catch (err) {
      toastError(err);
    }
  };

  function whoCanSee(): HTMLElement {
    const chosen: Visibility = calendar.visibility === 'private' ? 'private' : 'link';
    const radios = el(
      'div',
      { class: 'radio-list' },
      ...VISIBILITY.map((option) => {
        const input = el('input', {
          type: 'radio',
          name: 'visibility',
          value: option.value,
          checked: option.value === chosen,
        });
        input.addEventListener('change', () => void setVisibility(option.value));
        return el(
          'label',
          {},
          input,
          el('span', {}, el('b', { text: option.label }), el('small', { text: option.hint })),
        );
      }),
    );

    const link: Child[] = [];
    if (chosen !== 'private' && calendar.shareToken) {
      link.push(
        copyRow(shareUrl(calendar.shareToken), 'Copy link'),
        el(
          'div',
          { class: 'row' },
          button(
            'Replace link',
            async () => {
              const yes = await confirmDialog({
                title: 'Replace the share link?',
                description: 'The current link stops working straight away. Anyone still using it will need the new one.',
                confirmLabel: 'Replace link',
              });
              if (!yes) return;
              try {
                updated(await api.share(calendar.id, calendar.visibility, true));
                toast('New share link ready.');
              } catch (err) {
                toastError(err);
              }
            },
            { class: 'btn--small btn--quiet' },
          ),
        ),
      );
    } else {
      link.push(el('div', { class: 'empty', text: 'Pick “Anyone with the link” to get a link.' }));
    }
    return el('section', { class: 'stack' }, el('h3', { text: 'Who can see the ring' }), radios, ...link);
  }

  // One link per circle. Each opens that circle as a ring of its own, and each
  // is a capability in its own right: the radios above do not touch them.
  function oneCircle(): Child[] {
    const tags = options.tags?.() ?? [];
    if (!tags.length) return [];
    const nodes = flattenTree(buildCircleTree(tags)).filter((n) => n.tag);
    return [
      el('hr', { class: 'rule' }),
      el(
        'section',
        { class: 'stack' },
        el('h3', { text: 'Share one circle' }),
        el('p', {
          class: 'muted small',
          text: 'Each circle can have a link of its own. It opens that circle as a ring — the people in it and the circles inside it, nobody else — and it works whether or not the whole ring is shared.',
        }),
        el(
          'ul',
          { class: 'share-circles' },
          ...nodes.map((n) =>
            circleShareRow({
              calendarId: calendar.id,
              tag: n.tag!,
              depth: n.depth,
              onUpdated: async () => {
                await options.onTagsChanged?.();
                render();
              },
            }),
          ),
        ),
      ),
    ];
  }

  function subscribe(): Child[] {
    if (!calendar.icsToken) return [];
    const { https, webcal } = icsUrls(calendar.icsToken);
    const alarm = el('select', {}, ...ALARMS.map((a) => el('option', { value: a.value, text: a.label })));
    alarm.value = calendar.icsAlarm;
    alarm.addEventListener('change', () => {
      void (async () => {
        try {
          calendar = await api.patchCalendar(calendar.id, { icsAlarm: alarm.value as IcsAlarm });
          options.onUpdated(calendar);
          toast('Reminder setting saved.');
        } catch (err) {
          toastError(err);
          alarm.value = calendar.icsAlarm;
        }
      })();
    });

    return [
      el('hr', { class: 'rule' }),
      el(
        'section',
        { class: 'stack' },
        el('h3', { text: 'Subscribe from another calendar' }),
        el('p', {
          class: 'muted small',
          text: 'Subscribe once and every birthday shows up in your own calendar, updating as you change the ring.',
        }),
        copyRow(webcal, 'Copy'),
        el('div', { class: 'field__hint', text: 'Most apps open this straight away. If yours asks for a plain address, use:' }),
        copyRow(https, 'Copy'),
        el('div', { class: 'field' }, el('label', { text: 'Reminder' }), alarm),
        el(
          'details',
          {},
          el('summary', { class: 'small muted', text: 'How to add it' }),
          el(
            'ul',
            { class: 'small muted', style: 'padding-left:18px;margin:6px 0 0' },
            el('li', { text: 'Google Calendar: Other calendars, +, From URL, paste the plain address.' }),
            el('li', { text: 'Apple Calendar: File, New Calendar Subscription, paste the webcal address.' }),
            el('li', { text: 'Outlook: Add calendar, Subscribe from web, paste the plain address.' }),
          ),
        ),
        el(
          'div',
          { class: 'row' },
          button(
            'Replace subscribe address',
            async () => {
              const yes = await confirmDialog({
                title: 'Replace the subscribe address?',
                description: 'Anyone already subscribed stops receiving updates and has to subscribe again.',
                confirmLabel: 'Replace address',
              });
              if (!yes) return;
              try {
                updated(await api.rotateIcsToken(calendar.id));
                toast('New subscribe address ready.');
              } catch (err) {
                toastError(err);
              }
            },
            { class: 'btn--small btn--quiet' },
          ),
        ),
      ),
    ];
  }

  /**
   * The links that let people *in*: the collect links, one per collect source,
   * and the owner link. Neither shows the ring to anyone, which is why they sit
   * apart from the three above.
   */
  function waysIn(): Child[] {
    const collects = (options.sources?.() ?? []).filter((s) => s.kind === 'collect' && s.collectToken);
    return [
      el('hr', { class: 'rule' }),
      el(
        'section',
        { class: 'stack', 'data-role': 'collect-links' },
        el('h3', { text: 'Collect links' }),
        el('p', {
          class: 'muted small',
          text: collects.length
            ? 'Whoever opens one can add their own name and birthday. Nothing reaches the ring until you approve it, and they never see the ring.'
            : 'A collect link opens a form with one name and one date on it, for people to add themselves. Make one under Circles, where entries come from.',
        }),
        ...collects.map((source) =>
          el(
            'div',
            { class: 'stack share-collect', 'data-source': source.id },
            el('div', { class: 'list__name', text: source.name }),
            ...collectLinkControls(source, {
              calendarId: calendar.id,
              onUpdated: (next) => {
                options.onSourceUpdated?.(next);
                render();
              },
            }),
          ),
        ),
      ),
      el('hr', { class: 'rule' }),
      el(
        'section',
        { class: 'stack', 'data-role': 'owner-link' },
        el('h3', { text: 'The owner link' }),
        el('p', {
          class: 'muted small',
          text: 'Opens the ring for editing. Yours alone: whoever has it can change everything, and it is not something to hand out.',
        }),
        ownerLinkRow(calendar),
        ...ownerLinkActions({ calendar, onUpdated: updated }),
      ),
    ];
  }

  function content(): Child[] {
    return [whoCanSee(), ...oneCircle(), ...subscribe(), ...waysIn()];
  }

  const done = button('Done', () => handle.close(), { class: 'btn--primary' });
  handle.foot.append(el('span', { class: 'grow' }), done);
  handle.root.appendChild(handle.foot);
  render();
}
